import type { Question, SurveyPage } from '../components/QuestionBuilder'
import { lumi } from './lumi'

interface SurveyLibrarySyncPayload {
  surveyId: string
  surveyCode: string
  title: string
  description: string
  status: string
  languages: string[]
  startDate?: string
  endDate?: string
  targetAudience?: string
  tags: string[]
  questions: Question[]
  pages: SurveyPage[]
  logoUrl?: string
  logoDisplaySize?: 'small' | 'medium' | 'large' | 'original'
  createdAt?: string
  updatedAt?: string
}

interface SurveyLibraryRecord {
  _id?: string
  surveyId: string
  surveyCode: string
  surveyName: string
  fileName: string
  fileUrl: string
  version: number
  questionCount: number
  pageCount: number
  savedAt: string
  createdAt: string
  updatedAt: string
}

export interface SurveyLibrarySyncResult {
  recordId?: string
  fileName: string
  fileUrl: string
  version: number
  savedAt: string
}

const stripHtml = (value: string) => {
  if (typeof document === 'undefined') {
    return value.replace(/<[^>]+>/g, ' ')
  }

  const container = document.createElement('div')
  container.innerHTML = value
  return container.textContent || container.innerText || ''
}

const slugify = (value: string) =>
  stripHtml(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()

export const buildSurveyCode = (title: string) => {
  const base = slugify(title).toUpperCase().slice(0, 12) || 'PESQUISA'
  const stamp = new Date().toISOString().replace(/\D/g, '').slice(2, 14)
  return `DNA-${base}-${stamp}`
}

export const buildSurveyLibraryFileName = (title: string, surveyCode: string) => {
  const safeTitle = slugify(title) || 'pesquisa'
  return `${surveyCode}__${safeTitle}.txt`
}

const buildSurveySnapshot = (payload: SurveyLibrarySyncPayload, savedAt: string) =>
  JSON.stringify(
    {
      metadata: {
        surveyId: payload.surveyId,
        surveyCode: payload.surveyCode,
        surveyName: stripHtml(payload.title) || payload.title,
        status: payload.status,
        languages: payload.languages,
        startDate: payload.startDate || null,
        endDate: payload.endDate || null,
        targetAudience: payload.targetAudience || '',
        tags: payload.tags,
        logoUrl: payload.logoUrl || '',
        logoDisplaySize: payload.logoDisplaySize || 'medium',
        questionCount: payload.questions.length,
        pageCount: payload.pages.length,
        savedAt,
        createdAt: payload.createdAt || savedAt,
        updatedAt: payload.updatedAt || savedAt
      },
      survey: {
        title: payload.title,
        description: payload.description,
        logoDisplaySize: payload.logoDisplaySize || 'medium',
        questions: payload.questions,
        pages: payload.pages
      }
    },
    null,
    2
  )

const getSurveyLibrariesEntity = () => (lumi.entities as any).survey_libraries

export const syncSurveyLibrary = async (
  payload: SurveyLibrarySyncPayload
): Promise<SurveyLibrarySyncResult> => {
  const entity = getSurveyLibrariesEntity()

  if (!entity) {
    throw new Error('Entidade survey_libraries nao configurada no projeto.')
  }

  const savedAt = new Date().toISOString()
  const fileName = buildSurveyLibraryFileName(payload.title, payload.surveyCode)
  const snapshotText = buildSurveySnapshot(payload, savedAt)
  const snapshotFile = new File([snapshotText], fileName, {
    type: 'text/plain;charset=utf-8'
  })

  const uploadResult = await lumi.tools.file.upload([snapshotFile])
  const fileUrl = uploadResult?.[0]?.fileUrl

  if (!fileUrl) {
    throw new Error(uploadResult?.[0]?.uploadError || 'Falha ao enviar o arquivo da biblioteca.')
  }

  const response = await entity.list({
    filter: { surveyId: payload.surveyId },
    limit: 10
  })

  const existing = (response?.list || [])[0] as SurveyLibraryRecord | undefined
  const version = (existing?.version || 0) + 1

  const recordData: SurveyLibraryRecord = {
    surveyId: payload.surveyId,
    surveyCode: payload.surveyCode,
    surveyName: stripHtml(payload.title) || payload.title,
    fileName,
    fileUrl,
    version,
    questionCount: payload.questions.length,
    pageCount: payload.pages.length,
    savedAt,
    createdAt: existing?.createdAt || savedAt,
    updatedAt: savedAt
  }

  if (existing?._id) {
    await entity.update(existing._id, recordData)

    if (existing.fileUrl && existing.fileUrl !== fileUrl) {
      lumi.tools.file.delete([existing.fileUrl]).catch(() => undefined)
    }

    return {
      recordId: existing._id,
      fileName,
      fileUrl,
      version,
      savedAt
    }
  }

  const createdRecord = await entity.create(recordData)

  return {
    recordId: createdRecord?._id,
    fileName,
    fileUrl,
    version,
    savedAt
  }
}
